import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import navbar from '@/shared/components/ConsoleNavbar.module.css';
import { Icon } from '@/shared/components/Icon';
import { ReadingState } from '@/shared/components/ReadingState';

import { helpPageOfPath, manualIndexQueryOptions, manualPageQueryOptions } from './helpQueries';
import { decodeAnchor, INDEX_PAGE } from './markdown/paths';

import classes from './HelpNavbar.module.css';

/**
 * The menu of the manual (`/ayuda`): the console's menu gives way to the pages in the order of the
 * manual's own index, and the page being read lists its second-level titles under its entry, each
 * a link to its anchor. Like `AdminNavbar`, it replaces the main menu while the manual is open.
 * It reads through queries and never suspends: it sits above the content's boundary, and a menu
 * that suspended would blank the whole screen while a page loads.
 */
export function HelpNavbar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation('help');
  const location = useLocation();
  const current = helpPageOfPath(location.pathname);
  const hash = decodeAnchor(location.hash);
  const index = useQuery(manualIndexQueryOptions());
  // The titles of the page being read go under its entry, so the menu reads that page only once the
  // index lists it: an address the manual has no page for is not read here, and the content says so
  // ("Esa página del manual no existe"). The test is the index and not `hasManualPage`, which would
  // bring the manual's own module into the console's main bundle, where this menu lives.
  // This reading has no branch of its own (guía única, regla 8.1): the content reads the same page
  // under the same key and suspends on it, so while it loads the content shows the indicator, and
  // when it fails the content takes the error to the root route's boundary, which shows "No pudimos
  // cargar esta pantalla" in place of the whole console, this menu included (`RouteErrorPage`). A
  // notice here would never be seen.
  const listed = index.data?.pages.some((entry) => entry.page === current) === true;
  const page = useQuery({ ...manualPageQueryOptions(current), enabled: listed });

  const sections =
    page.data === undefined || current === INDEX_PAGE
      ? []
      : page.data.headings.filter((heading) => heading.level === 2);

  return (
    <nav className={navbar.navbar} aria-label={t('menu.aria')}>
      <Link
        to="/"
        className={navbar.item}
        style={{ marginTop: 8, color: 'var(--sl-nav-muted)' }}
        onClick={onNavigate}
      >
        <Icon name="arrowLeft" />
        <span className={navbar.label}>{t('menu.back')}</span>
      </Link>

      <Link
        to="/ayuda"
        className={navbar.item}
        activeProps={{ className: `${navbar.item} ${navbar.active}` }}
        activeOptions={{ exact: true }}
        style={{ marginTop: 8 }}
        onClick={onNavigate}
      >
        <Icon name="book" />
        <span className={navbar.label}>{t('menu.index')}</span>
      </Link>

      {/* The pages of the menu are the manual's index (guía única, reglas 8.1 a 8.3): the indicator
          while it loads, and the notice when it could not be read, both on the white surface they have
          in the content (the indicator is drawn in the steel of the menu itself, and the notice's tint
          is translucent and its text dark: on the menu neither would be seen). */}
      <div className={index.isPending || index.isError ? classes.notice : undefined}>
        <ReadingState
          what={t('index.what')}
          isLoading={index.isPending}
          error={index.error}
          onRetry={() => void index.refetch()}
        >
          {(index.data?.groups ?? []).map((group) => (
            <div key={group.title ?? ''}>
              {group.title === null ? null : (
                <div className={`${navbar.sectionLabel} ${classes.groupLabel}`}>{group.title}</div>
              )}
              {group.entries.map((entry) => (
                <div key={entry.page}>
                  <Link
                    to="/ayuda/$"
                    params={{ _splat: entry.page }}
                    className={navbar.item}
                    activeProps={{ className: `${navbar.item} ${navbar.active}` }}
                    activeOptions={{ exact: true }}
                    title={entry.label}
                    onClick={onNavigate}
                  >
                    <span className={navbar.label}>{entry.label}</span>
                  </Link>
                  {entry.page === current && sections.length > 0 ? (
                    <ul className={classes.sections}>
                      {sections.map((section) => (
                        <li key={section.id}>
                          <Link
                            to="/ayuda/$"
                            params={{ _splat: entry.page }}
                            hash={section.id}
                            className={
                              section.id === hash
                                ? `${classes.section} ${classes.current}`
                                : classes.section
                            }
                            title={section.text}
                            onClick={onNavigate}
                          >
                            {section.text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </ReadingState>
      </div>
      <div className={navbar.spacer} />
    </nav>
  );
}
